import Sticky from "react-stickynode"
import styled from "styled-components"
import { themeGet } from "@styled-system/theme-get"
import axios from "axios"
import ReactMarkdown from "react-markdown"
import { serverSideTranslations } from "next-i18next/serverSideTranslations"
import parse from "html-react-parser"
import Image from "next/image"
import qs from "qs"
import axiosRetry from "axios-retry"
import { useRouter } from "next/router"

import { getApiUrlLocale, getLocaleRoute } from "@/helpers"
import ResetCSS from "@/common/assets/css/style"
import NavbarNew from "@/containers/common/NavbarNew"
import Footer from "@/containers/common/FooterNew"
import SectionHeading from "@/common/components/SectionHeading"
import SectionLine from "@/common/components/SectionLine"
import MetaData from "@/common/components/MetaData"
import Container from "@/common/components/UI/Container"
import { AppWrapper, ContentWrapper } from "@/containers/common/style"

axiosRetry(axios, { retries: 3 })

export const getStaticPaths = async () => {
  const query = qs.stringify(
    {
      locale: "*",
      populate: {
        event_identifier: {
          fields: ["identifier"]
        }
      },
      filters: {
        event_identifier: {
          identifier: {
            $notNull: true
          }
        }
      },
      pagination: {
        pageSize: 99
      }
    },
    {
      encodeValuesOnly: true
    }
  )
  const apiUrl = `${process.env.NEXT_PUBLIC_CMS_HOST}/api/events?${query}`
  let res
  try {
    res = await axios.get(apiUrl)
  } catch (e) {
    throw new Error(`Failed to fetch events, url: ${apiUrl}, err: ${e}`)
  }
  const data = res.data
  const paths =
    data?.data?.map(event => {
      return {
        params: {
          identifier:
            event.attributes["event_identifier"]?.data?.attributes?.identifier
        },
        locale: getLocaleRoute(event.attributes.locale)
      }
    }) || []

  return { paths, fallback: "blocking" }
}

export const getStaticProps = async ({ locale, params }) => {
  const identifier = params.identifier

  const query = qs.stringify(
    {
      filters: {
        event_identifier: {
          identifier: {
            $eq: identifier
          }
        }
      },
      locale: getApiUrlLocale(locale),
      populate: {
        cover_image: {
          fields: ["url"]
        }
      }
    },
    {
      encodeValuesOnly: true
    }
  )
  const apiUrl = `${process.env.NEXT_PUBLIC_CMS_HOST}/api/events?${query}`
  let res
  try {
    res = await axios.get(apiUrl)
  } catch (e) {
    throw new Error(`Failed to fetch event, url: ${apiUrl}, err: ${e}`)
  }

  const { data } = res

  if (data?.data?.length === 0)
    return {
      redirect: {
        destination: `/${locale}/events`,
        permanent: false
      }
    }

  const metadata = {
    title: `${data?.data[0]?.attributes?.name} - KryptoGO Events`,
    description: data?.data[0]?.attributes?.description,
    img: data?.data[0]?.attributes?.cover_image?.data?.attributes.url
  }
  return {
    props: {
      identifier,
      event: data,
      meta: metadata,
      ...(await serverSideTranslations(locale, ["common", "contactUs"]))
    },
    revalidate: 300
  }
}

const EventDetail = ({ identifier, event, meta }) => {
  const __content = event?.data[0]?.attributes.content
  const __iframe = __content.replace(
    /iframeS/g,
    "<div className='videoBox'><iframe"
  )
  const content = __iframe.replace(/iframeE/g, "></iframe></div>")
  const { locale } = useRouter()
  let contentType = "markdown"

  if (
    content.includes("<h2") ||
    content.includes("<p") ||
    content.includes("<br") ||
    content.includes("<span") ||
    content.includes("<iframe")
  ) {
    contentType = "html"
  }

  const renderContent =
    contentType === "html" ? (
      parse(content)
    ) : (
      <ReactMarkdown>{event?.data[0]?.attributes.content}</ReactMarkdown>
    )

  return (
    <>
      <MetaData
        title={meta.title}
        description={meta.description}
        url={`https://www.kryptogo.com/${locale}/events/${identifier}`}
        image={meta.img}
      />

      <ResetCSS />
      {/* end of global and reset style */}

      {/* start app classic landing */}
      <AppWrapper>
        <Sticky top={0} innerZ={1000} activeClass="sticky-active">
          <NavbarNew />
        </Sticky>
        <Container>
          <article>
            <TitleWrapper>
              <SectionHeading
                as="h1"
                title={event?.data[0]?.attributes?.name}
              />
            </TitleWrapper>
            <ContentWrapper>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: "center",
                  alignItems: "center",
                  maxWidth: "990px",
                  margin: "0 auto"
                }}
              >
                <ImageWrapper>
                  <Image
                    alt=""
                    src={
                      event?.data[0]?.attributes?.cover_image?.data?.attributes
                        .url
                    }
                    fill
                    sizes="100vw"
                    style={{
                      objectFit: "cover"
                    }}
                  />
                </ImageWrapper>
                <ArticleContent>{renderContent}</ArticleContent>
              </div>
            </ContentWrapper>
          </article>
        </Container>
        <SectionLine />
        <Footer />
      </AppWrapper>
    </>
  )
}

const VideoBox = styled.div``

const TitleWrapper = styled.div`
  padding-top: 160px;
  @media (max-width: 768px) {
    padding-top: 100px;
  }
`
const ArticleContent = styled.div`
  // Delete this if there's no font issue
  // @import url("https://fonts.googleapis.com/css2?family=Lora&family=Noto+Sans+SC:wght@300;400;700&family=Noto+Sans+TC:wght@300;400;700&family=Noto+Serif+TC:wght@300;400;700&display=swap");
  word-wrap: break-word;
  overflow: visible;
  width: 100%;
  padding-bottom: 30px !important;
  overflow: visible;

  h1 {
    font-family: "Noto Sans SC", "Noto Serif TC", Arial, sans-serif;
    font-family: "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
    font-weight: 700;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: rgba(255, 255, 255, 1);
    color: ${themeGet("colors.primary")};
    text-shadow:
      0px 0 20px rgba(255, 191, 17, 0.2),
      0 0 60px rgba(255, 194, 17, 0.5);
    font-size: 2rem;
    min-width: 80%;
    display: flex;
    position: relative;
    margin-top: 90px;
    margin-bottom: 2rem;
    letter-spacing: 1px;
    line-height: 2.5rem;
    margin-left: -25px;

    &:after,
    &:before {
      content: "";
      color: #ffc211;
      background-color: ${themeGet("colors.primary")};
      height: 0.5px;
      flex: 1;
      margin: 0 0 0 1rem;
    }

    &::before {
      margin: 0 1rem 0 0;
    }

    strong {
      max-width: 80%;
      text-align: center;
    }

    @media (max-width: 730px) {
      font-size: 1.2rem;
      margin-top: 70px;
      margin-bottom: 0px;
      letter-spacing: 0px;
      line-height: 1.6rem;
      font-weight: 700;

      &:after,
      &:before {
        content: "";
        color: #ffc211;
        background-color: ${themeGet("colors.primary")};
        height: 0.5px;
        flex: 1;
        margin: 0 0 0 0.5rem;
      }

      &:before {
        margin: 0 0.5rem 0 0;
      }
    }
  }

  h2,
  h3,
  h4,
  h5,
  h6 {
    font-family: "Noto Sans SC", "Noto Serif TC", Arial, sans-serif;
    font-family: "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
    font-weight: 700;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    color: rgba(255, 255, 255, 1);
    color: ${themeGet("colors.primary")};
    text-shadow:
      0px 0 20px rgba(255, 191, 17, 0.2),
      0 0 60px rgba(255, 194, 17, 0.5);
    color: rgba(255, 255, 255, 1);
    color: ${themeGet("colors.primary")};
    font-size: 1.5rem;
    margin-top: 69px;
    margin-bottom: 2rem;
    line-height: 33.18px;
    letter-spacing: 1px;
    overflow: visible;

    @media (max-width: 730px) {
      font-size: 1rem;
      margin-top: 40px;
      margin-bottom: 1rem;
      letter-spacing: 0px;
      line-height: 1.4rem;
      font-weight: 600;
      justify-content: center;
      text-align: center;

      strong {
        max-width: 90%;
        font-weight: 700;
      }
    }
  }

  u {
    text-decoration: none;
  }

  u,
  a {
    color: ${themeGet("colors.primary")};
    color: rgba(255, 255, 255, 1);
    padding-bottom: 3px;
    font-weight: 300;
    opacity: 1;
    font-weight: 300;
    transition: 0.3s ease;
    border: 0;
    position: relative;
    background: linear-gradient(
        ${themeGet("colors.primary")},
        ${themeGet("colors.primary")}
      )
      no-repeat left 120%/100% 21%;

    &:before {
      content: "";
      width: calc(100% + 3px);
      height: 130%;
      bottom: 0;
      left: -1.5px;
      position: absolute;
      background: linear-gradient(
        0deg,
        ${themeGet("colors.primary")} 0%,
        ${themeGet("colors.primary")}30 60%,
        ${themeGet("colors.primary")}00 100%
      );
      opacity: 0;
      transition: 0.8s;
      z-index: -1;
    }

    &:hover {
      text-shadow: 0px 1px 8px black;

      &:before {
        opacity: 1;
      }
    }

    strong {
      color: white !important;
    }
  }

  img {
    width: 100%;
    max-width: 100%;
    object-fit: contain;
    max-height: 90vh;
    margin: 37px auto 20px auto;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  }

  figcaption {
    // background-color: #fff;
    font-size: 16px;
    text-align: center;
    margin-top: -5px;
  }

  blockquote {
    margin-top: 30px;
    display: block;
    padding-left: 20px;
    margin-left: -23px;
    padding-bottom: 5px;
    position: relative;
    margin-right: 0;

    &:before {
      content: "";
      /* background: linear-gradient(
        to right,
        ${themeGet("colors.primary")}60 0%,
        ${themeGet("colors.primary")}03 80%,
        ${themeGet("colors.primary")}00 100%
      ); */
      position: absolute;
      border-left: 4px solid ${themeGet("colors.primary")};
      width: 52px;
      height: calc(100% + 16px);
      top: -8px;
      left: 0px;

      @media (max-width: 768px) {
        left: 0px;
      }
    }

    @media (max-width: 768px) {
      margin-left: 0px;
      padding-left: 20px;
    }
  }

  p {
    font-size: 1.25rem;
    font-family: "lora", "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
    letter-spacing: 0.05rem;
    line-height: 2rem;
    position: relative;
    font-weight: 300;
    color: rgba(255, 255, 255, 0.9);
    margin-top: 1.2rem;
    margin-bottom: 40x;

    @media (max-width: 730px) {
      font-size: 1rem;
      margin-top: 1.2rem;
      margin-bottom: -8.28px;
      line-height: 28px;
      font-weight: 300;
    }

    strong {
      font-weight: 500;
      color: ${themeGet("colors.primary")};
    }
  }

  ul {
    list-style: none;
    padding-left: 0px;
    // margin-top: 42px;
    margin-bottom: 42px;

    li {
      font-size: 1.25rem;
      font-family: "lora", "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
      letter-spacing: 0.05rem;
      line-height: 2rem;
      position: relative;
      font-weight: 300;
      color: rgba(255, 255, 255, 0.9);
      margin-top: 1.2rem;
      margin-bottom: 40x;

      @media (max-width: 730px) {
        font-size: 1rem;
        margin-top: 1.2rem;
        margin-bottom: -8.28px;
        line-height: 28px;
        font-weight: 300;
      }

      strong {
        font-weight: 500;
        color: ${themeGet("colors.primary")};
      }

      margin: 40px 0 -9.2px 30px;

      @media (max-width: 730px) {
        margin: 40px 0 -9.2px 20px;
      }

      position: relative;

      &:before {
        content: "";
        position: absolute;
        display: inline-block;
        width: 0.4em;
        height: 0.4em;
        margin-left: -1rem;
        border-radius: 1em;
        background-color: rgba(255, 255, 255, 1);
        background-color: ${themeGet("colors.primary")};
        margin-right: 1rem;
        top: 15px;
        transform: translateY(-50%);
      }
    }
  }

  ol {
    padding-left: 0px;
    counter-reset: li;
    margin-bottom: 42px;

    li {
      font-size: 1.25rem;
      font-family: "lora", "Noto Sans SC", "Noto Sans TC", Arial, sans-serif;
      letter-spacing: 0.05rem;
      line-height: 2rem;
      position: relative;
      font-weight: 300;
      color: rgba(255, 255, 255, 0.9);
      margin-top: 1.2rem;
      margin-bottom: 40x;

      @media (max-width: 730px) {
        font-size: 1rem;
        margin-top: 1.2rem;
        margin-bottom: -8.28px;
        line-height: 28px;
        font-weight: 300;
      }

      strong {
        font-weight: 500;
        color: ${themeGet("colors.primary")};
      }

      margin: 40px 0 -9.2px 30px;

      @media (max-width: 730px) {
        margin: 40px 0 -9.2px 20px;
      }

      counter-increment: li;

      &:before {
        content: counter(li);
        display: inline-block;
        position: absolute;
        font-weight: 700;
        width: 1rem;
        height: 0.4em;
        margin-left: -1.2rem;
        border-radius: 1em;
        margin-right: 1rem;
        color: rgba(255, 255, 255, 1);
        color: ${themeGet("colors.primary")};
      }
    }
  }
  .videoBox {
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 56.25%;
    iframe {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
    }
  }
`
const ImageWrapper = styled.div`
  position: relative;
  max-width: 1500px;
  width: 100%;
  padding-top: 50%;
`

export default EventDetail
